import React from "react";
import { direccionTexto } from "@/lib/etapas";
import { activas, misEtapas, citaEn, horarioEn, TIPO_DESCARGA_EN, docDeEtapa } from "@/lib/chofer";
import { Button } from "@/components/ui/button";
import { abrirArchivo } from "@/components/chofer/AgregarArchivo";
import { ArrowRight, ChevronRight, FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): «Trip Recap». Resumen de la asignación antes de empezar (y durante el viaje, sin reiniciar nada):
// contenedor, tamaño, cita, y cada parada con destino, dirección completa, horario y referencias.
function pistaEtapa(carga, e) {
  const p = [];
  if (e.referencia) p.push(e.referencia);
  if (e.clave === "delivery") {
    if (e.tipo_descarga) p.push(t(TIPO_DESCARGA_EN[e.tipo_descarga]));
    if (docDeEtapa(carga, "delivery").requerido) p.push(t("Signed DO / POD required"));
  }
  if (e.clave === "rail_pickup") p.push(t("Enter Delivery ETA before departure."));
  if (e.clave === "return_empty" && docDeEtapa(carga, "return_empty").requerido) p.push(t("Empty return J1 required"));
  return p.join(" · ");
}
export function TripRecap({ carga, uid, nombreChofer, miTurno, iniciado, terminada, soloLectura = false, empezando, onEmpezar, onVolver, onEtapa }) {
  const ets = misEtapas(carga, uid);
  const entrega = ets.find((e) => e.clave === "delivery");
  const cita = entrega ? citaEn(entrega) : "";
  const doc = carga.delivery_order;
  const otros = (carga.documentos || []).filter((d) => d.tipo !== "delivery_order");
  const primera = ets[0];
  return (
    <section className="bg-white border border-line rounded-[14px] p-4" data-testid="trip-recap">
      <p className="eyebrow text-navy-2">{t("Trip recap · Driver {nombreChofer}", { nombreChofer })}</p>
      <h2 className="mt-1 font-display font-bold text-[26px] leading-tight text-navy-2">{t("Your assignment")}</h2>
      <p className="mt-2 text-[16px] text-ink" data-testid="recap-contenedor">{t("Container: {numero_contenedor}{v1}", { numero_contenedor: carga.numero_contenedor, v1: carga.tamano ? ` · ${carga.tamano}` : "" })}</p>
      {cita && <p className="text-[16px] text-ink" data-testid="recap-cita">{t("Appointment: {cita}", { cita })}</p>}
      {carga.notas && <p className="mt-2 text-[14px] bg-surface-2 border border-line rounded-md p-2.5 text-ink-2" data-testid="recap-notas">{carga.notas}</p>}
      <ol className="mt-4 space-y-4">
        {ets.map((e, i) => {
          const dir = direccionTexto(e);
          const hor = e.clave === "delivery" ? citaEn(e) : horarioEn(e);
          const pista = pistaEtapa(carga, e);
          return (
            <li key={e.clave}>
              <button type="button" data-testid={`recap-etapa-${e.clave}`} onClick={() => onEtapa(e.clave)} className="w-full text-left flex items-start gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block font-display font-bold text-[18px] text-navy-2">{i + 1}. {t(e.nombre)}</span>
                  <span className={cn("block font-bold text-[17px]", e.es_yarda_propia ? "text-gold" : "text-navy-2")} data-testid={`recap-lugar-${e.clave}`}>{e.lugar || t("Location not set")}</span>
                  {dir && <span className="block text-[15px] text-ink">{dir}</span>}
                  {hor && <span className="block text-[13.5px] text-ink-2">{hor}</span>}
                  {pista && <span className="block text-[13.5px] text-ink">{pista}</span>}
                  {e.ubicacion_notas && <span className="block mt-1 whitespace-pre-wrap text-[14px] text-ink-2" data-testid="recap-notas-ubicacion"><strong>{t("Location notes")}: </strong>{e.ubicacion_notas}</span>}
                  {e.salida_at && <span className="block text-[12.5px] font-semibold text-ok">{t("Completed")}</span>}
                </span>
                <ChevronRight className="h-5 w-5 text-ink-3 mt-1 shrink-0" />
              </button>
            </li>
          );
        })}
      </ol>
      {otros.length > 0 && (
        <div className="mt-4" data-testid="recap-documentos">
          <p className="eyebrow">{t("Documents from the office")}</p>
          <div className="mt-1.5 space-y-1.5">
            {otros.map((d) => (
              <button key={d.file_id} type="button" onClick={() => abrirArchivo(d.file_id)} className="w-full flex items-center gap-2 text-left rounded-lg border border-line bg-surface-2 px-3 py-2 min-h-[44px] text-[14.5px] text-ink">
                <FileText className="h-4 w-4 text-ink-3 shrink-0" /><span className="truncate">{d.nombre}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {!soloLectura && !iniciado && !miTurno && primera && (
        <p className="mt-4 text-[14px] text-ink-2 bg-surface-2 border border-line rounded-lg p-3" data-testid="recap-espera">{t("Waiting for the first step to be ready. You will be able to start when it is your turn.")}</p>
      )}
      <div className="mt-4 grid grid-cols-[1fr_1.6fr] gap-2">
        <Button type="button" variant="outline" data-testid="recap-do-btn" disabled={!doc} onClick={() => doc && abrirArchivo(doc.file_id)} className="h-[52px] text-[16px] font-bold text-navy-2">{t("DO · PDF")}</Button>
        {soloLectura || (!iniciado && !miTurno) ? (
          <div />
        ) : !iniciado ? (
          <Button type="button" data-testid="recap-start-btn" disabled={empezando} onClick={onEmpezar} className="h-[52px] text-[16px] font-bold">
            {empezando ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("Start Trip ")}<ArrowRight className="h-5 w-5" /></>}
          </Button>
        ) : (
          <Button type="button" data-testid="recap-volver-btn" variant="outline" onClick={onVolver} className="h-[52px] text-[16px] font-bold text-navy-2">
            {terminada ? t("Back to trip summary") : t("Return to current step")} <ArrowRight className="h-5 w-5" />
          </Button>
        )}
      </div>
      {!doc && <p className="mt-1.5 text-[12.5px] text-ink-3" data-testid="recap-sin-do">{t("The Delivery Order has not been uploaded yet.")}</p>}
    </section>
  );
}
